import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, DatePicker, TimePicker, Checkbox, Typography, List, Space, AutoComplete, Tag } from "antd";
import { EditOutlined, DeleteOutlined, UserAddOutlined, ClockCircleOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AdminInviteEmployeeModal from "./AdminInviteEmployeeModal";
import AdminEditRequestModal from "./AdminEditRequestModal";
import AdminConfirmRemovalModal from "./AdminConfirmRemovalModal";
import { useOuberService } from "../../../../services";
import { showErrorToast, showSuccessToast } from "../../ToastNotification";
import { getUnitAcronym, cleanName, getActiveEarliestUnit } from "../../../../utils/ouberUtils";


dayjs.extend(customParseFormat);

const { Option } = Select;
const { Text, Title } = Typography;

const getStatusTagClass = (status) => {
  switch (status) {
    case "Approved by AO": return "tag-approved";
    case "Completed Trip": return "tag-completed";
    case "Requested to Join": return "tag-pending";
    case "Declined by AO":
    case "Cancelled by AO":
    case "Cancelled by User": return "tag-cancelled";
    default: return "";
  }
};

const formatTime12h = (timeStr) => {
  if (!timeStr) return "N/A";
  // Handle already formatted AM/PM (ensure case insensitive check)
  if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) return timeStr;
  
  // Robust parsing: try multiple formats including 24h and abbreviated 24h
  const formats = ["HH:mm:ss", "HH:mm", "h:mm a", "H:mm:ss", "H:mm", "HH:mm A"];
  const dj = dayjs(timeStr, formats);
  return dj.isValid() ? dj.format("h:mm a") : timeStr;
};

const AdminEditTripModal = ({ open, onCancel, onSave, initialValues, onDeleteTrip, onUpdateSuccess }) => {
  const [form] = Form.useForm();
  const isMultipleAllowed = Form.useWatch('isMultiplePickupAllowed', form);
  const { service: ouberService } = useOuberService();
  
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [existingVenues] = useState([
    { value: 'UPOU HQ' },
    { value: 'UP Los Baños' },
    { value: 'Crossing LB' },
    { value: 'Olivarez LB' },
    { value: 'UP Diliman' },
    { value: 'UP Manila' },
    { value: 'UP BGC' },
    { value: 'CHED Main Office' },
    { value: 'UPOU Learning Center Manila' },
    { value: 'NAIA Terminal 1' },
    { value: 'NAIA Terminal 2' },
    { value: 'NAIA Terminal 3' },
    { value: 'NAIA Terminal 4' },
    { value: 'PITX' },
    { value: 'Clark International Airport' },
  ]);

  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengers, setPassengers] = useState(initialValues?.passengers || []);

  const ouberServiceRef = useRef(ouberService);
  useEffect(() => {
    ouberServiceRef.current = ouberService;
  }, [ouberService]);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        const parsedCapacity = initialValues.capacity 
          ? parseInt(String(initialValues.capacity).split('/')[1] || initialValues.capacity) 
          : (initialValues.trip_passenger_capacity || 1);

        form.resetFields();
        form.setFieldsValue({
          ...initialValues,
          date: initialValues.trip_date ? dayjs(initialValues.trip_date) : null,
          time: initialValues.pickup_time ? dayjs(initialValues.pickup_time, ["HH:mm:ss", "h:mm a", "HH:mm"]) : null,
          estimatedArrival: initialValues.estimated_arrival_time ? dayjs(initialValues.estimated_arrival_time, ["HH:mm:ss", "h:mm a", "HH:mm"]) : null,
          pickup: initialValues.pickup_venue || initialValues.pickup,
          destination: initialValues.dropoff_location || initialValues.destination,
          purpose: initialValues.trip_purpose || initialValues.purpose,
          isMultiplePickupAllowed: !!(initialValues.allowed_multiple_pickup || initialValues.isMultiplePickupAllowed),
          isCustomDriver: initialValues.driver_id === null && !!initialValues.custom_driver_name,
          isCustomVehicle: initialValues.vehicle_id === null && !!initialValues.custom_vehicle_name,
          deadlineDate: initialValues.deadline_date ? dayjs(initialValues.deadline_date) : ((initialValues.date || initialValues.trip_date) ? dayjs(initialValues.date || initialValues.trip_date).subtract(1, 'day') : null),
          deadlineTime: initialValues.deadline_time ? dayjs(initialValues.deadline_time, ["HH:mm:ss", "h:mm a"]) : dayjs("11:59 pm", "h:mm a"),
          vehicleType: initialValues.vehicleType || initialValues.custom_vehicle_type || (initialValues.vehicle && typeof initialValues.vehicle === 'object' ? initialValues.vehicle.vehicle_type : (initialValues.vehicle_type || undefined)),
          contactNumber: initialValues.contactNumber || (initialValues.driver && typeof initialValues.driver === 'object' ? initialValues.driver.contact_number : undefined) || initialValues.custom_driver_contact,
          capacity: parsedCapacity > 0 ? parsedCapacity : 1,
          custom_driver_name: initialValues.custom_driver_name,
          customNumber: initialValues.custom_driver_contact,
          custom_vehicle_name: initialValues.custom_vehicle_name,
          custom_vehicle_plate: initialValues.custom_vehicle_plate,
        });
        if (initialValues.passengers) {
          setPassengers(initialValues.passengers);
        }
      } else {
        form.setFieldsValue({
          isMultiplePickupAllowed: false,
        });
        setPassengers([]);
      }

      const fetchRegistries = async () => {
        try {
          // Fetch drivers and vehicles in parallel
          const [drvs, vehs] = await Promise.all([
            ouberServiceRef.current.getDrivers(),
            ouberServiceRef.current.getVehicles()
          ]);
          
          setDrivers(Array.isArray(drvs) ? drvs : (drvs?.data || []));
          setVehicles(Array.isArray(vehs) ? vehs : (vehs?.data || []));
        } catch (error) {
          console.error("Failed to fetch drivers or vehicles", error);
        }
      };
      fetchRegistries();
    }
  }, [open, initialValues, form]);

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleSubmit = (values) => {
    const formattedValues = {
      trip_date: values.date ? values.date.format("YYYY-MM-DD") : null,
      pickup_time: values.time ? values.time.format("HH:mm:ss") : null,
      estimated_arrival_time: values.estimatedArrival ? values.estimatedArrival.format("HH:mm:ss") : null,
      trip_passenger_capacity: values.capacity,
      allowed_multiple_pickup: values.isMultiplePickupAllowed ? 1 : 0,
      trip_purpose: sanitizeInput(values.purpose),
      remarks: sanitizeInput(values.purpose),
      driver_id: values.isCustomDriver ? null : (values.driver_id || null),
      vehicle_id: values.isCustomVehicle ? null : (values.vehicle_id || null),
      driver_contact: !values.isCustomDriver ? sanitizeInput(values.contactNumber) : null,
      vehicle_type: !values.isCustomVehicle ? sanitizeInput(values.vehicleType) : null,
      custom_driver_name: values.isCustomDriver ? sanitizeInput(values.custom_driver_name) : null,
      custom_driver_contact: values.isCustomDriver ? sanitizeInput(values.customNumber) : null,
      custom_vehicle_name: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_name) : null,
      custom_vehicle_plate: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_plate) : null,
      custom_vehicle_type: values.isCustomVehicle ? sanitizeInput(values.vehicleType) : null,
      pickup_venue: sanitizeInput(values.pickup),
      dropoff_location: sanitizeInput(values.destination),
      passengers: sortedPassengers.map(p => {
        const pTime = p.estimated_pickup_time || p.pivot?.estimated_pickup_time;
        const pVenue = p.pickup_venue || p.pivot?.pickup_venue;
        
        return {
          trip_request_id: p.trip_request_id || p.pivot?.trip_request_id,
          id: p.employee_id || p.id,
          is_external: !!p.is_external,
          external_passenger_name: p.is_external ? sanitizeInput(p.name || p.external_passenger_name) : null,
          external_passenger_email: p.is_external ? sanitizeInput(p.email || p.external_passenger_email) : null,
          status: p.status || p.request_status || 'Approved by AO',
          estimated_pickup_time: pTime ? (dayjs(pTime, ["HH:mm:ss", "h:mm a", "H:mm:ss"]).isValid() ? dayjs(pTime, ["HH:mm:ss", "h:mm a", "H:mm:ss"]).format("HH:mm:ss") : pTime) : null,
          pickup_venue: sanitizeInput(pVenue),
          remarks: sanitizeInput(p.remarks || ""),
          unit: p.unit || p.representing_unit || null,
        };
      }),
      deadline_date: values.deadlineDate ? values.deadlineDate.format("YYYY-MM-DD") : null,
      deadline_time: values.deadlineTime ? values.deadlineTime.format("HH:mm:ss") : null,
    };
    onSave(formattedValues);
    form.resetFields();
  };

  const handleOpenEditPassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setIsEditRequestOpen(true);
  };

  const handleOpenRemovePassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setIsConfirmRemoveOpen(true);
  };

  const sortedPassengers = useMemo(() => {
    const tripTimeStr = form.getFieldValue('time') ? form.getFieldValue('time').format("HH:mm:ss") : (initialValues?.pickup_time || "00:00:00");
    const requestsArr = Array.isArray(initialValues?.requests) 
      ? initialValues.requests 
      : (typeof initialValues?.requests === 'object' && initialValues.requests !== null 
          ? Object.values(initialValues.requests) 
          : []);

    return [...passengers].sort((a, b) => {
      const getPTime = (p) => {
        const foundReq = requestsArr.find(r => {
          const pReqId = p.trip_request_id || p.pivot?.trip_request_id;
          const rReqId = r.trip_request_id || r.id;
          
          // Strict ID matching if we have a specific request ID
          if (pReqId && rReqId) {
            return String(pReqId) === String(rReqId);
          }

          const pIds = [p.employee_id, p.id, p.pivot?.employee_id, p.user_id].filter(Boolean);
          const rIds = [r.employee_id, r.id, r.user_id].filter(Boolean);
          return pIds.some(pid => rIds.some(rid => String(pid) === String(rid)));
        });

        const raw = p.estimated_pickup_time || p.pivot?.estimated_pickup_time || foundReq?.estimated_pickup_time;
        return raw || tripTimeStr;
      };

      const timeA = getPTime(a);
      const timeB = getPTime(b);
      
      return dayjs(timeA, ["HH:mm:ss", "h:mm a", "H:mm:ss"]).diff(dayjs(timeB, ["HH:mm:ss", "h:mm a", "H:mm:ss"]));
    });
  }, [passengers, initialValues, form]);


  const formBaseline = useMemo(() => {
    if (!initialValues) return null;
    const parsedCapacity = initialValues.capacity 
      ? parseInt(String(initialValues.capacity).split('/')[1] || initialValues.capacity) 
      : (initialValues.trip_passenger_capacity || 1);

    return {
      date: initialValues.trip_date ? dayjs(initialValues.trip_date).format('YYYY-MM-DD') : null,
      time: initialValues.pickup_time ? dayjs(initialValues.pickup_time, ["HH:mm:ss", "h:mm a", "HH:mm"]).format("HH:mm:ss") : null,
      estimatedArrival: initialValues.estimated_arrival_time ? dayjs(initialValues.estimated_arrival_time, ["HH:mm:ss", "h:mm a", "HH:mm"]).format("HH:mm:ss") : null,
      pickup: initialValues.pickup_venue || initialValues.pickup,
      destination: initialValues.dropoff_location || initialValues.destination,
      purpose: initialValues.trip_purpose || initialValues.purpose,
      remarks: initialValues.remarks,
      driverId: initialValues.driver_id || null,
      vehicleId: initialValues.vehicle_id || null,
      contactNumber: initialValues.contactNumber || (initialValues.driver && typeof initialValues.driver === 'object' ? initialValues.driver.contact_number : undefined) || initialValues.custom_driver_contact || "",
      vehicleType: initialValues.vehicleType || initialValues.custom_vehicle_type || (initialValues.vehicle && typeof initialValues.vehicle === 'object' ? initialValues.vehicle.vehicle_type : (initialValues.vehicle_type || undefined)) || "",
      capacity: parsedCapacity > 0 ? parsedCapacity : 1,
      isMultiplePickupAllowed: !!(initialValues.allowed_multiple_pickup || initialValues.isMultiplePickupAllowed),
      deadlineDate: initialValues.deadline_date 
        ? dayjs(initialValues.deadline_date).format('YYYY-MM-DD') 
        : ((initialValues.trip_date || initialValues.date) ? dayjs(initialValues.trip_date || initialValues.date).subtract(1, 'day').format('YYYY-MM-DD') : null),
      deadlineTime: initialValues.deadline_time 
        ? dayjs(initialValues.deadline_time, ["HH:mm:ss", "h:mm a"]).format("HH:mm:ss") 
        : dayjs("11:59 pm", "h:mm a").format("HH:mm:ss"),
      passengers: initialValues.passengers || []
    };
  }, [initialValues]);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsDirty(false);
    }
  }, [open]);

  const handleValuesChange = (_, allValues) => {
    if (!formBaseline) return;
    
    const currentDate = allValues.date ? allValues.date.format('YYYY-MM-DD') : null;
    const currentTime = allValues.time ? allValues.time.format("HH:mm:ss") : null;
    const currentArrival = allValues.estimatedArrival ? allValues.estimatedArrival.format("HH:mm:ss") : null;
    const currentDeadlineDate = allValues.deadlineDate ? allValues.deadlineDate.format('YYYY-MM-DD') : null;
    const currentDeadlineTime = allValues.deadlineTime ? allValues.deadlineTime.format("HH:mm:ss") : null;

    let dirty = false;
    if (currentDate !== formBaseline.date) dirty = true;
    else if (currentTime !== formBaseline.time) dirty = true;
    else if (currentArrival !== formBaseline.estimatedArrival) dirty = true;
    else if ((allValues.pickup || "") !== (formBaseline.pickup || "")) dirty = true;
    else if ((allValues.destination || "") !== (formBaseline.destination || "")) dirty = true;
    else if ((allValues.purpose || "") !== (formBaseline.purpose || "")) dirty = true;
    else if ((allValues.remarks || "") !== (formBaseline.remarks || "")) dirty = true;
    else if ((allValues.driver_id ? String(allValues.driver_id) : null) !== (formBaseline.driverId ? String(formBaseline.driverId) : null)) dirty = true;
    else if ((allValues.vehicle_id ? String(allValues.vehicle_id) : null) !== (formBaseline.vehicleId ? String(formBaseline.vehicleId) : null)) dirty = true;
    else if ((allValues.contactNumber || "") !== (formBaseline.contactNumber || "")) dirty = true;
    else if ((allValues.vehicleType || "") !== (formBaseline.vehicleType || "")) dirty = true;
    else if (Number(allValues.capacity || 0) !== Number(formBaseline.capacity)) dirty = true;
    else if (!!allValues.isMultiplePickupAllowed !== !!formBaseline.isMultiplePickupAllowed) dirty = true;
    else if (currentDeadlineDate !== formBaseline.deadlineDate) dirty = true;
    else if (currentDeadlineTime !== formBaseline.deadlineTime) dirty = true;

    setIsDirty(dirty);
  };

  const handleDeleteClick = () => {
    if (onDeleteTrip && initialValues) {
      onDeleteTrip(initialValues.trip_id || initialValues.id);
    }
  };

  return (
    <>
      <Modal
        open={open}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Title level={4} style={{ color: 'white', margin: 0, fontSize: '18px' }}>Edit Trip Details</Title>
            <Space size="middle" style={{ display: 'flex', alignItems: 'center' }}>
              {!["Completed Trip", "Completed", "Cancelled by AO", "Cancelled by User"].includes(initialValues?.status || initialValues?.trip_status) && (
                <DeleteOutlined style={{ color: "white", cursor: "pointer", fontSize: "18px" }} onClick={handleDeleteClick} />
              )}
              <CloseOutlined style={{ color: "white", cursor: "pointer", fontSize: "18px", marginLeft: '4px' }} onClick={onCancel} />
            </Space>
          </div>
        }
        styles={{
          header: {
            backgroundColor: "#862633",
            padding: "16px 24px",
            borderRadius: "8px 8px 0 0",
            margin: "-20px -24px 24px -24px" 
          },
          title: {
            color: "white",
            margin: 0
          },
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            margin: '0 -24px',
            padding: '12px 24px'
          }
        }}
        onCancel={onCancel}
        footer={null}
        className="ouber-modal white-close-icon"
        width={700}
        zIndex={1060}
        closable={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Form 
            form={form} 
            layout="vertical" 
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item name="date" label="Trip Date" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                <DatePicker 
                  style={{ width: '100%' }} 
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
              <Form.Item name="capacity" label="Passenger Capacity" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }, { type: 'number', min: 1, message: "Must be at least 1" }]}>
                <InputNumber 
                  min={1} 
                  precision={0}
                  style={{ width: '100%' }} 
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item 
                name="time" 
                label="Pickup Time" 
                style={{ flex: 1 }} 
                rules={[{ required: true, message: "Input cannot be empty" }]}
              >
                <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item 
                noStyle 
                shouldUpdate={(prev, curr) => prev.time !== curr.time}
              >
                {({ getFieldValue }) => (
                  <Form.Item 
                    name="estimatedArrival" 
                    label="Estimated Arrival" 
                    style={{ flex: 1 }} 
                    dependencies={['time']}
                    rules={[
                      { required: true, message: "Input cannot be empty" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const pickup = getFieldValue('time');
                          if (!value || !pickup) return Promise.resolve();
                          
                          const pMin = pickup.hour() * 60 + pickup.minute();
                          const aMin = value.hour() * 60 + value.minute();
                          
                          if (aMin > pMin) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Arrival must be after pickup time"));
                        },
                      }),
                    ]}
                  >
                    <TimePicker 
                      use12Hours 
                      format="h:mm a" 
                      style={{ width: '100%' }} 
                      disabledTime={() => {
                        const pickup = getFieldValue('time');
                        if (!pickup) return {};
                        return {
                          disabledHours: () => Array.from({ length: pickup.hour() }, (_, i) => i),
                          disabledMinutes: (h) => h === pickup.hour() ? Array.from({ length: pickup.minute() }, (_, i) => i) : [],
                        };
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item 
                noStyle 
                shouldUpdate={(prev, curr) => prev.date !== curr.date}
              >
                {({ getFieldValue }) => (
                  <Form.Item 
                    name="deadlineDate" 
                    label="Deadline Date" 
                    style={{ flex: 1 }} 
                    dependencies={['date']}
                    rules={[
                      { required: true, message: "Input cannot be empty" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const tripDate = getFieldValue('date');
                          if (!value || !tripDate || value.isBefore(tripDate, 'day') || value.isSame(tripDate, 'day')) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Deadline cannot be after the trip date"));
                        },
                      }),
                    ]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      disabledDate={(current) => {
                        const tripDate = getFieldValue('date');
                        if (!tripDate) return current && current < dayjs().startOf('day');
                        return current && (current.isAfter(tripDate, 'day') || current < dayjs().startOf('day'));
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
              <Form.Item 
                noStyle 
                shouldUpdate={(prev, curr) => prev.deadlineDate !== curr.deadlineDate || prev.time !== curr.time}
              >
                {({ getFieldValue }) => (
                  <Form.Item 
                    name="deadlineTime" 
                    label="Deadline Time" 
                    style={{ flex: 1 }} 
                    dependencies={['deadlineDate', 'date', 'time']}
                    rules={[
                      { required: true, message: "Input cannot be empty" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const deadlineDate = getFieldValue('deadlineDate');
                          const tripDate = getFieldValue('date');
                          const pickupTime = getFieldValue('time');
                          
                          if (!value || !deadlineDate || !tripDate || !pickupTime) {
                            return Promise.resolve();
                          }
                          
                          if (deadlineDate.isSame(tripDate, 'day')) {
                            const dMin = value.hour() * 60 + value.minute();
                            const pMin = pickupTime.hour() * 60 + pickupTime.minute();
                            
                            if (dMin >= pMin) {
                              return Promise.reject(new Error("Deadline must be before pickup time on the same date"));
                            }
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker 
                      use12Hours 
                      format="h:mm a" 
                      style={{ width: '100%' }} 
                      disabledTime={() => {
                        const dDate = getFieldValue('deadlineDate');
                        const tDate = getFieldValue('date');
                        const pickup = getFieldValue('time');
                        if (!dDate || !tDate || !pickup || !dDate.isSame(tDate, 'day')) return {};
                        return {
                          disabledHours: () => {
                            const hours = [];
                            const startHour = pickup.minute() === 0 ? pickup.hour() : pickup.hour() + 1;
                            for (let i = startHour; i < 24; i++) hours.push(i);
                            return hours;
                          },
                          disabledMinutes: (h) => {
                            if (h === pickup.hour()) {
                              const mins = [];
                              for (let i = pickup.minute(); i < 60; i++) mins.push(i);
                              return mins;
                            }
                            return [];
                          }
                        };
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Form.Item name="isCustomDriver" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Driver not in registry?</Checkbox>
              </Form.Item>
            </div>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.isCustomDriver !== currentValues.isCustomDriver}
            >
              {({ getFieldValue }) => {
                const isCustomDriver = getFieldValue('isCustomDriver');
                return isCustomDriver ? (
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="custom_driver_name" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                      <Input placeholder="Enter driver name" />
                    </Form.Item>
                    <Form.Item 
                      name="customNumber" 
                      label="Contact Number" 
                      style={{ flex: 1 }}
                      rules={[
                        { required: true, message: "Input cannot be empty" }, 
                        { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                      ]}
                    >
                      <Input placeholder="E.g. 09123456789" maxLength={11} />
                    </Form.Item>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="driver_id" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                      <Select 
                        showSearch
                        placeholder="Select a driver..."
                        optionFilterProp="children"
                        onChange={(val) => {
                          const selectedDriver = drivers.find(d => d.driver_id === val);
                          if (selectedDriver) {
                            form.setFieldsValue({ contactNumber: selectedDriver.contact_number });
                          }
                        }}
                      >
                        {drivers.sort((a, b) => a.driver_name.localeCompare(b.driver_name)).map(d => (
                          <Option key={d.driver_id} value={d.driver_id}>{d.driver_name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item 
                      name="contactNumber" 
                      label="Contact Number" 
                      style={{ flex: 1 }}
                      rules={[
                        { required: true, message: "Input cannot be empty" }, 
                        { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                      ]}
                    >
                      <Input placeholder="E.g. 09123456789" maxLength={11} disabled />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Form.Item name="isCustomVehicle" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Vehicle not in registry?</Checkbox>
              </Form.Item>
            </div>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.isCustomVehicle !== currentValues.isCustomVehicle}
            >
              {({ getFieldValue }) => {
                const isCustomVehicle = getFieldValue('isCustomVehicle');
                return isCustomVehicle ? (
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="custom_vehicle_name" label="Vehicle Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                      <Input placeholder="Enter vehicle name" />
                    </Form.Item>
                    <Form.Item name="custom_vehicle_plate" label="Plate Number" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                      <Input placeholder="Enter plate number" />
                    </Form.Item>
                    <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                      <Select>
                        <Option value="University Owned">University Owned</Option>
                        <Option value="Car Rental">Car Rental</Option>
                        <Option value="Carpool">Carpool</Option>
                        <Option value="Outsourced">Outsourced</Option>
                      </Select>
                    </Form.Item>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="vehicle_id" label="Vehicle" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                      <Select 
                        showSearch
                        placeholder="Select a vehicle..."
                        optionFilterProp="children"
                        onChange={(val) => {
                          const selectedVehicle = vehicles.find(v => v.vehicle_id === val);
                          if (selectedVehicle) {
                            form.setFieldsValue({ 
                              vehicleType: selectedVehicle.vehicle_type,
                              capacity: selectedVehicle.max_passenger_capacity
                            });
                          }
                        }}
                      >
                        {vehicles.sort((a, b) => a.vehicle_model.localeCompare(b.vehicle_model)).map(v => (
                          <Option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_model} ({v.plate_number})</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                      <Select disabled>
                        <Option value="University Owned">University Owned</Option>
                        <Option value="Car Rental">Car Rental</Option>
                        <Option value="Carpool">Carpool</Option>
                        <Option value="Outsourced">Outsourced</Option>
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item name="pickup" label="Pickup Venue" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                <AutoComplete
                  options={existingVenues}
                  placeholder="Select or type venue..."
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              </Form.Item>
              <Form.Item name="destination" label="Dropoff Location" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                <AutoComplete
                  options={existingVenues}
                  placeholder="Select or type location..."
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              </Form.Item>
            </div>
            
            <Form.Item name="isMultiplePickupAllowed" valuePropName="checked" style={{ marginBottom: '16px' }}>
              <Checkbox>Allow Multiple Pickup Venues</Checkbox>
            </Form.Item>

            <Form.Item name="purpose" label="Purpose of Trip" rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
              <Input.TextArea rows={3} placeholder="Purpose" />
            </Form.Item>

            <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Text strong>Passengers</Text>
                <Button 
                  type="dashed" 
                  icon={<UserAddOutlined />} 
                  onClick={() => setIsInviteOpen(true)}
                >
                  Invite Passengers
                </Button>
              </div>

              <List
                size="small"
                bordered
                dataSource={sortedPassengers}
                style={{ maxHeight: '200px', overflowY: 'auto' }}
                renderItem={passenger => {
                  const requestsArr = Array.isArray(initialValues?.requests) 
                    ? initialValues.requests 
                    : (typeof initialValues?.requests === 'object' && initialValues.requests !== null 
                        ? Object.values(initialValues.requests) 
                        : []);

                  const request = requestsArr.find(r => {
                    // Match by direct request ID first - this is the only definitive match
                    const pReqId = passenger.trip_request_id || passenger.pivot?.trip_request_id;
                    const rReqId = r.trip_request_id || r.id;
                    if (pReqId && rReqId) {
                      return String(pReqId) === String(rReqId);
                    }

                    // Fallback to ID matching only if we don't have a specific request ID to check for
                    const pIdArr = [passenger.employee_id, passenger.id, passenger.pivot?.employee_id, passenger.user_id].filter(Boolean);
                    const rIdArr = [r.employee_id, r.id, r.user_id].filter(Boolean);
                    if (pIdArr.some(pid => rIdArr.some(rid => String(pid) === String(rid)))) return true;
                    
                    const pEmail = (passenger.email || passenger.external_passenger_email || "").toLowerCase().trim();
                    const rEmail = (r.email || r.external_passenger_email || "").toLowerCase().trim();
                    if (pEmail && rEmail && pEmail === rEmail) return true;

                    // Fallback to name matching
                    const pName = (passenger.name || `${passenger.first_name || ""} ${passenger.last_name || ""}`.trim() || "").toLowerCase().trim();
                    const rName = (r.name || r.external_passenger_name || r.display_name || "").toLowerCase().trim();
                    return pName && rName && (pName.includes(rName) || rName.includes(pName));
                  });

                  const pTimeRaw = passenger.estimated_pickup_time || 
                                   passenger.pivot?.estimated_pickup_time || 
                                   request?.estimated_pickup_time;

                  const pVenueRaw = passenger.pickup_venue || 
                                    passenger.pivot?.pickup_venue || 
                                    request?.pickup_venue;
                  
                  // Prepare enriched passenger object for modala
                  const enrichedPassenger = {
                    ...passenger,
                    estimated_pickup_time: pTimeRaw,
                    pickup_venue: pVenueRaw,
                    remarks: passenger.remarks || request?.remarks || passenger.pivot?.remarks,
                    trip_request_id: passenger.trip_request_id || request?.trip_request_id || passenger.pivot?.trip_request_id,
                    request_status: passenger.status || passenger.request_status || request?.request_status || passenger.pivot?.request_status
                  };

                    const rawUnit = getActiveEarliestUnit(passenger);

                    return (
                      <List.Item
                        actions={[
                          !["Cancelled by AO", "Cancelled by User", "Declined by AO", "Completed Trip"].includes(enrichedPassenger.request_status) && <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenEditPassenger(enrichedPassenger)} />,
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleOpenRemovePassenger(enrichedPassenger)} />
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          title={
                            <>
                              {cleanName(passenger.first_name || passenger.name) + " " + cleanName(passenger.last_name || "")} 
                              {!passenger.is_external && (
                                <Text type="secondary" style={{ fontWeight: 400, fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                                  ({getUnitAcronym(rawUnit) !== "N/A" ? getUnitAcronym(rawUnit) : getUnitAcronym(passenger.first_name || passenger.name)})
                                </Text>
                              )}
                            {passenger.is_external && <Tag color="orange" style={{ border: 'none', marginLeft: '4px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>Guest</Tag>}
                            <Tag className={getStatusTagClass(enrichedPassenger.request_status)} style={{ border: 'none', marginLeft: '8px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                              {enrichedPassenger.request_status}
                            </Tag>
                          </>
                        }
                        description={
                          (() => {
                            const tTimeVal = form.getFieldValue('time');
                            const tTimeStr = tTimeVal ? tTimeVal.format("h:mm a") : formatTime12h(initialValues?.pickup_time);
                            const tVenue = (form.getFieldValue('pickup') || initialValues?.pickup_venue || initialValues?.pickup || "").toString().trim();

                            const pTimeFmt = pTimeRaw ? formatTime12h(pTimeRaw) : null;
                            const pVenue = (pVenueRaw || "").toString().trim();
                            
                            const finalTime = (pTimeFmt && pTimeFmt !== "N/A" && pTimeFmt !== "Invalid Date") ? pTimeFmt : tTimeStr;
                            const finalVenue = (pVenue && pVenue !== "") ? pVenue : tVenue;

                            if (!isMultipleAllowed) return null;

                            return (
                              <div style={{ fontSize: '13px', color: '#000000', marginTop: '4px', fontWeight: 500 }}>
                                <ClockCircleOutlined style={{ marginRight: '4px', fontSize: '12px' }} />
                                {finalTime} 
                                <span style={{ margin: '0 8px', color: '#d9d9d9' }}>•</span> 
                                {finalVenue}
                              </div>
                            );
                          })()
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" className="ouber-btn-forest" disabled={!isDirty}>
                Confirm Edit
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      <AdminInviteEmployeeModal 
        open={isInviteOpen}
        onCancel={() => setIsInviteOpen(false)}
        onAddEmployees={async (emps) => {
          const currentCount = sortedPassengers.length;
          const totalCapacity = initialValues?.trip_passenger_capacity || initialValues?.capacity || 0;
          const available = totalCapacity - currentCount;

          if (emps.length > available) {
            showErrorToast("Error", `Trip is already full or has only ${available} seats left.`);
            return;
          }

          try {
            const addedFormatted = [];
            for (const emp of emps) {
              const payload = {
                trip_id: initialValues.trip_id,
                employee_id: emp.is_external ? null : emp.id,
                external_passenger_name: emp.is_external ? emp.name : null,
                external_passenger_email: emp.is_external ? emp.email : null,
                request_status: 'Approved by AO',
                trip_date: initialValues.trip_date || initialValues.date,
                pickup_venue: initialValues.pickup_venue || initialValues.pickup,
                dropoff_location: initialValues.dropoff_location || initialValues.destination,
                trip_purpose: initialValues.trip_purpose || initialValues.purpose || 'Official Business'
              };
              
              const response = await ouberService.createTripRequest(payload);
              
              addedFormatted.push({
                ...emp,
                trip_request_id: response.trip_request_id,
                status: 'Approved by AO',
                pickup_venue: payload.pickup_venue,
                request_status: 'Approved by AO',
                is_external: !!emp.is_external
              });
            }
            
            showSuccessToast("Success", `${emps.length > 1 ? emps.length + " employees" : "Employee"} added to trip!`);
            setPassengers(prev => [...prev, ...addedFormatted]);
            setIsInviteOpen(false);
            if (onUpdateSuccess) onUpdateSuccess();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not add one or more employees.";
            showErrorToast("Error", errorMsg);
            setIsInviteOpen(false);
            throw e;
          }
        }}
        maxSelectable={Math.max(0, Number(initialValues?.trip_passenger_capacity || initialValues?.capacity || 0) - sortedPassengers.length)}
        existingPassengerIds={passengers.map(p => p.id || p.employee_id)}
      />

      <AdminEditRequestModal 
        open={isEditRequestOpen}
        onCancel={() => setIsEditRequestOpen(false)}
        passengerData={selectedPassenger}
        isMultiplePickupAllowed={form.getFieldValue('isMultiplePickupAllowed')}
        tripStartTime={form.getFieldValue('time')}
        tripArrivalTime={form.getFieldValue('estimatedArrival')}
        onSave={async (updatedData) => {
          try {
            // Priority 1: Direct ID in payload
            // Priority 2: Original ID in selection
            // Priority 3: Lookup in cached requests
            const requestId = updatedData.trip_request_id || 
                             selectedPassenger?.trip_request_id ||
                             (selectedPassenger?.pivot?.trip_request_id) ||
                             (initialValues?.requests?.find(r => Number(r.employee_id) === Number(selectedPassenger?.id || updatedData.id) && r.request_status !== 'Declined')?.trip_request_id);
            
            if (requestId) {
              await ouberService.updateRequestStatus(requestId, updatedData);
              showSuccessToast("Updated", "Passenger status and remarks saved successfully.");
              
              if (updatedData.request_status === 'Declined' || updatedData.request_status === 'Declined by AO') {
                setPassengers(prev => prev.filter(p => {
                  const isMatch = (Number(p.trip_request_id) === Number(requestId)) || 
                                  (p.id && String(p.id) === String(updatedData.id)) ||
                                  (p.employee_id && String(p.employee_id) === String(updatedData.id));
                  return !isMatch;
                }));
              } else {
                setPassengers(prev => prev.map(p => {
                  const isMatch = (Number(p.trip_request_id) === Number(requestId)) || 
                                  (p.id && String(p.id) === String(updatedData.id)) ||
                                  (p.employee_id && String(p.employee_id) === String(updatedData.id));
                  if (isMatch) {
                      return { 
                        ...p, 
                        ...updatedData,
                        status: updatedData.request_status,
                        estimated_pickup_time: updatedData.estimated_pickup_time || p.estimated_pickup_time,
                        pickup_venue: updatedData.pickup_venue || p.pickup_venue
                      };
                  }
                  return p;
                }));
              }
              setSelectedPassenger(null);
              setIsEditRequestOpen(false);
              if (onUpdateSuccess) onUpdateSuccess();
            } else {
              showErrorToast("Error", "Could not find a valid database record (Trip Request) for this passenger.");
            }
          } catch (e) {
            console.error("Update error:", e);
            const errorMsg = e.response?.data?.message || "Could not save passenger changes to the database.";
            showErrorToast("Update Failed", errorMsg);
            setSelectedPassenger(null);
            setIsEditRequestOpen(false);
            throw e;
          }
        }}




      />

      <AdminConfirmRemovalModal 
        open={isConfirmRemoveOpen}
        onCancel={() => setIsConfirmRemoveOpen(false)}
        passengerData={selectedPassenger}
        onConfirm={async (passenger) => {
          try {
            const requestId = passenger?.trip_request_id || 
                             (passenger?.pivot?.trip_request_id) ||
                             (initialValues?.requests?.find(r => Number(r.employee_id) === Number(passenger.id || passenger.employee_id) && r.request_status !== 'Declined')?.trip_request_id);
            
            if (requestId) {
              await ouberService.updateRequestStatus(requestId, { 
                request_status: 'Declined by AO',
                remarks: passenger.remarks // <-- PASS REMARKS HERE
              });
              showSuccessToast("Removed", "Passenger removed from trip immediately.");
            }
            
            setPassengers(prev => prev.filter(p => (p.id || p.employee_id) !== (passenger.id || passenger.employee_id)));
            setIsConfirmRemoveOpen(false);
            if (onUpdateSuccess) onUpdateSuccess();
          } catch (e) {
            console.error("Removal error:", e);
            showErrorToast("Error", "Could not remove passenger from database.");
            setSelectedPassenger(null);
            setIsConfirmRemoveOpen(false);
            throw e;
          }
        }}
      />
    </>
  );
};

export default AdminEditTripModal;
